import React, { useEffect, useMemo, useState } from "react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { cn } from "../lib/utils"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {})
    },
    ...options
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

function money(cents, currency = "USD") {
  if (cents === null || cents === undefined) return "—"
  return `${currency} ${(Number(cents) / 100).toFixed(2)}`
}

export function BidComparisonPanel({ requisitionId, canEnter = true, onClose }) {
  const [data, setData] = useState(null)
  const [tab, setTab] = useState("overview")
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [selections, setSelections] = useState({})
  const [customerPo, setCustomerPo] = useState({ order_number: "", order_date: "", total_cents: "" })
  const [clarifyVendorRfqId, setClarifyVendorRfqId] = useState("")
  const [clarifyBody, setClarifyBody] = useState("")
  const [sellPrices, setSellPrices] = useState({})
  const [receiptDrafts, setReceiptDrafts] = useState({})

  async function load() {
    setLoading(true)
    setError("")
    try {
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/comparison`)
      setData(payload)
      const next = {}
      ;(payload.line_matrix || []).forEach((row) => {
        const lineId = row.line.id
        next[lineId] = row.awarded_quote_id || row.lowest_quote_id || ""
      })
      setSelections(next)
      const prices = {}
      ;(payload.proposed_invoice_lines || []).forEach((row) => {
        prices[row.purchase_requisition_line_id] = String(row.suggested_sell_cents ?? row.cost_cents ?? 0)
      })
      setSellPrices(prices)
      const receipts = {}
      ;(payload.vendor_pos || []).forEach((po) => {
        ;(po.lines || []).forEach((line) => {
          receipts[line.id] = String(line.quantity_received ?? "0")
        })
      })
      setReceiptDrafts(receipts)
      if (payload.customer_po) {
        setCustomerPo({
          order_number: payload.customer_po.order_number || "",
          order_date: payload.customer_po.order_date || "",
          total_cents: payload.customer_po.total_cents != null ? String(payload.customer_po.total_cents) : ""
        })
      } else if (payload.requisition) {
        setCustomerPo({
          order_number: payload.requisition.customer_po_number || "",
          order_date: payload.requisition.customer_po_date || "",
          total_cents: ""
        })
      }
    } catch (e) {
      setError(e.message || "Failed to load comparison")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [requisitionId])

  const currency = data?.requisition?.currency || "USD"
  const readOnly = Boolean(data?.read_only)

  const vendorCount = useMemo(() => {
    const ids = new Set(Object.values(selections).filter(Boolean).map(String))
    return ids.size
  }, [selections])

  async function awardSelected() {
    setSaving(true)
    setError("")
    try {
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/award`, {
        method: "POST",
        body: JSON.stringify({ selections })
      })
      setData(payload)
    } catch (e) {
      setError(e.message || "Award failed")
    } finally {
      setSaving(false)
    }
  }

  async function awardAll(quoteId) {
    setSaving(true)
    setError("")
    try {
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/award`, {
        method: "POST",
        body: JSON.stringify({ award_all_to_quote_id: quoteId })
      })
      setData(payload)
      const next = {}
      ;(payload.line_matrix || []).forEach((row) => {
        next[row.line.id] = row.awarded_quote_id || quoteId
      })
      setSelections(next)
    } catch (e) {
      setError(e.message || "Award failed")
    } finally {
      setSaving(false)
    }
  }

  async function createVendorPos() {
    setSaving(true)
    setError("")
    try {
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/create_vendor_pos`, {
        method: "POST",
        body: JSON.stringify({})
      })
      setData(payload.comparison)
    } catch (e) {
      setError(e.message || "Could not create vendor POs")
    } finally {
      setSaving(false)
    }
  }

  async function linkCustomerPo() {
    setSaving(true)
    setError("")
    try {
      await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/link_customer_po`, {
        method: "POST",
        body: JSON.stringify({
          order_number: customerPo.order_number,
          order_date: customerPo.order_date,
          total_cents: customerPo.total_cents ? Number(customerPo.total_cents) : null
        })
      })
      await load()
    } catch (e) {
      setError(e.message || "Could not link customer PO")
    } finally {
      setSaving(false)
    }
  }

  async function sendClarification() {
    if (!clarifyVendorRfqId || !clarifyBody.trim()) {
      setError("Select a vendor RFQ and enter a message.")
      return
    }
    setSaving(true)
    setError("")
    try {
      await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/rfq_clarifications`, {
        method: "POST",
        body: JSON.stringify({ vendor_rfq_id: Number(clarifyVendorRfqId), body: clarifyBody.trim() })
      })
      setClarifyBody("")
      await load()
    } catch (e) {
      setError(e.message || "Could not send clarification")
    } finally {
      setSaving(false)
    }
  }

  async function markReceived(purchaseOrderId, { full = false } = {}) {
    setSaving(true)
    setError("")
    try {
      const po = (data.vendor_pos || []).find((p) => p.id === purchaseOrderId)
      let receipts = undefined
      if (!full && po) {
        receipts = {}
        ;(po.lines || []).forEach((line) => {
          receipts[line.id] = {
            quantity_received: receiptDrafts[line.id] ?? line.quantity_received ?? 0
          }
        })
      }
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/mark_vendor_po_received`, {
        method: "POST",
        body: JSON.stringify({ purchase_order_id: purchaseOrderId, receipts })
      })
      setData(payload)
      const nextReceipts = { ...receiptDrafts }
      ;(payload.vendor_pos || []).forEach((p) => {
        ;(p.lines || []).forEach((line) => {
          nextReceipts[line.id] = String(line.quantity_received ?? "0")
        })
      })
      setReceiptDrafts(nextReceipts)
    } catch (e) {
      setError(e.message || "Could not mark received")
    } finally {
      setSaving(false)
    }
  }

  async function createVendorBill(purchaseOrderId) {
    setSaving(true)
    setError("")
    try {
      const po = (data.vendor_pos || []).find((p) => p.id === purchaseOrderId)
      const hasReceipt = (po?.lines || []).some((l) => Number(l.quantity_received) > 0)
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/create_vendor_bill`, {
        method: "POST",
        body: JSON.stringify({
          purchase_order_id: purchaseOrderId,
          mark_received: !hasReceipt,
          bill_received_only: true
        })
      })
      setData(payload.comparison)
    } catch (e) {
      setError(e.message || "Could not create vendor bill")
    } finally {
      setSaving(false)
    }
  }

  async function createSalesInvoice() {
    setSaving(true)
    setError("")
    try {
      const line_amounts = {}
      Object.entries(sellPrices).forEach(([lineId, cents]) => {
        line_amounts[lineId] = Number(cents || 0)
      })
      const payload = await fetchJson(`/api/purchases/purchase_requisitions/${requisitionId}/create_sales_invoice`, {
        method: "POST",
        body: JSON.stringify({ line_amounts })
      })
      setData(payload.comparison)
    } catch (e) {
      setError(e.message || "Could not create sales invoice")
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading bid comparison…</p>
  if (!data) return <p className="text-sm text-red-600">{error || "No comparison data"}</p>

  const req = data.requisition
  const quotes = data.quotes || []

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Bid Comparison — {req.heading || req.requisition_number}</h2>
          <p className="text-sm text-muted-foreground">
            {req.requisition_number} · {req.kind_label || "Requisition"} · {req.customer?.name} · {quotes.length} quote{quotes.length === 1 ? "" : "s"}
            {req.quote_deadline ? ` · Deadline ${req.quote_deadline}` : ""}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Status: <span className="font-medium text-foreground">{req.status}</span>
            {data.recommendation ? ` · Recommended: ${data.recommendation.vendor_name}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Back to requisition
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.open(`/api/purchases/purchase_requisitions/${requisitionId}/comparison_pdf`, "_blank", "noopener,noreferrer")}
          >
            Export PDF
          </Button>
          {canEnter && !readOnly && data.recommendation ? (
            <Button type="button" disabled={saving} onClick={() => awardAll(data.recommendation.quote_id)}>
              Approve recommended
            </Button>
          ) : null}
          {canEnter && !readOnly ? (
            <Button type="button" disabled={saving} onClick={awardSelected}>
              {vendorCount > 1 ? "Split & approve" : "Approve selected"}
            </Button>
          ) : null}
          {canEnter && (req.status === "quote_approved" || req.status === "split") ? (
            <Button type="button" disabled={saving} onClick={createVendorPos}>
              Issue vendor PO(s)
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {readOnly ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Quote comparison is locked after award. Vendor totals show selected line items only. Customer PO details stay internal.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {[
          ["overview", "Overview"],
          ["items", `Item Comparison (${(data.line_matrix || []).length})`],
          ["commercial", "Commercial"],
          ["delivery", "Delivery"],
          ["attachments", `Attachments (${(data.attachments || []).length})`],
          ["clarifications", `Clarifications (${(data.clarifications || []).length})`],
          ["pos", "Linked POs"]
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cn(
              "rounded-md px-3 py-1.5 text-sm",
              tab === key ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
            )}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <div className="grid gap-4">
          {data.recommendation ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Award recommendation</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium">{data.recommendation.vendor_name}</p>
                <p className="text-muted-foreground">
                  {data.recommendation.reason} — {money(data.recommendation.grand_total_cents, currency)}
                </p>
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">No submitted vendor quotes yet.</p>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quote totals (all vendors)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="px-2 py-2">#</th>
                      <th className="px-2 py-2">Vendor</th>
                      <th className="px-2 py-2">Lead time</th>
                      <th className="px-2 py-2">ExW</th>
                      <th className="px-2 py-2">Subtotal</th>
                      <th className="px-2 py-2">Discount</th>
                      <th className="px-2 py-2">Other</th>
                      <th className="px-2 py-2">Shipping</th>
                      <th className="px-2 py-2">Grand total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q, idx) => {
                      const isLowest =
                        data.recommendation?.quote_id === q.id ||
                        (quotes[0] && quotes[0].id === q.id && quotes.length > 1)
                      return (
                      <tr
                        key={q.id}
                        className={cn("border-b border-border/70", isLowest && "bg-emerald-50/80")}
                      >
                        <td className="px-2 py-2">{idx + 1}</td>
                        <td className="px-2 py-2 font-medium">
                          {q.vendor_name}
                          {isLowest ? (
                            <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">
                              Lowest
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">{q.lead_time || "—"}</td>
                        <td className="px-2 py-2">{q.ex_work || "—"}</td>
                        <td className="px-2 py-2">{money(q.subtotal_cents, q.currency)}</td>
                        <td className="px-2 py-2">{money(q.discount_cents, q.currency)}</td>
                        <td className="px-2 py-2">{money(q.other_charges_cents, q.currency)}</td>
                        <td className="px-2 py-2">{money(q.shipping_charges_cents, q.currency)}</td>
                        <td className="px-2 py-2 font-semibold">{money(q.grand_total_cents, q.currency)}</td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {tab === "items" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Line-by-line comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2">Qty</th>
                    {quotes.map((q) => (
                      <th key={q.id} className="px-2 py-2">
                        {q.vendor_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.line_matrix || []).map((row, idx) => (
                    <tr key={row.line.id} className="border-b border-border/70 align-top">
                      <td className="px-2 py-2">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="font-medium">{row.line.display_name || row.line.description}</div>
                        <div className="text-xs text-muted-foreground">
                          {(row.line.display_meta || [row.line.brand, row.line.product_name].filter(Boolean)).join(" · ")}
                        </div>
                        {row.line.service_details ? (
                          <div className="text-xs text-muted-foreground">{row.line.service_details}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        {row.line.quantity} {row.line.unit}
                      </td>
                      {row.cells.map((cell) => {
                        const isLowest = row.lowest_quote_id === cell.quote_id
                        const selected = String(selections[row.line.id] || "") === String(cell.quote_id)
                        return (
                          <td key={`${row.line.id}-${cell.quote_id}`} className="px-2 py-2">
                            <label className="grid gap-1">
                              <span className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`supply-${row.line.id}`}
                                  disabled={!canEnter || readOnly || cell.unit_price_cents == null}
                                  checked={selected}
                                  onChange={() =>
                                    setSelections((s) => ({ ...s, [row.line.id]: cell.quote_id }))
                                  }
                                />
                                <span className={cn(isLowest && "rounded bg-emerald-100 px-1 font-medium text-emerald-900")}>
                                  {cell.unit_price_cents == null ? "—" : money(cell.total_cents, currency)}
                                </span>
                              </span>
                              {cell.remarks ? <span className="text-xs text-muted-foreground">{cell.remarks}</span> : null}
                            </label>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {tab === "commercial" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Commercial terms</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-2 py-2">Vendor</th>
                    <th className="px-2 py-2">Payment terms</th>
                    <th className="px-2 py-2">Packing</th>
                    <th className="px-2 py-2">Other</th>
                    <th className="px-2 py-2">Shipping</th>
                    <th className="px-2 py-2">Discount</th>
                    <th className="px-2 py-2">Validity</th>
                    <th className="px-2 py-2">T&amp;C</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-b border-border/70 align-top">
                      <td className="px-2 py-2 font-medium">{q.vendor_name}</td>
                      <td className="px-2 py-2">{q.payment_terms || "—"}</td>
                      <td className="px-2 py-2">{money(q.packing_charges_cents, q.currency)}</td>
                      <td className="px-2 py-2">{money(q.other_charges_cents, q.currency)}</td>
                      <td className="px-2 py-2">{money(q.shipping_charges_cents, q.currency)}</td>
                      <td className="px-2 py-2">{money(q.discount_cents, q.currency)}</td>
                      <td className="px-2 py-2">{q.valid_until || "—"}</td>
                      <td className="px-2 py-2 max-w-[220px] whitespace-pre-wrap text-xs">{q.terms_and_conditions || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {tab === "delivery" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Delivery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-2 py-2">Vendor</th>
                    <th className="px-2 py-2">Lead time</th>
                    <th className="px-2 py-2">Delivery terms</th>
                    <th className="px-2 py-2">ExW</th>
                    <th className="px-2 py-2">Place ready</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-b border-border/70">
                      <td className="px-2 py-2 font-medium">{q.vendor_name}</td>
                      <td className="px-2 py-2">{q.lead_time || "—"}</td>
                      <td className="px-2 py-2">{q.delivery_terms || "—"}</td>
                      <td className="px-2 py-2">{q.ex_work || "—"}</td>
                      <td className="px-2 py-2">{q.place_ready_for_shipping || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {tab === "attachments" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attachments</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.attachments || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No vendor attachments yet.</p>
            ) : (
              <ul className="grid gap-2 text-sm">
                {data.attachments.map((att) => (
                  <li key={att.id} className="rounded-lg border border-border px-3 py-2">
                    <div className="font-medium">
                      {att.title} · {att.vendor_name}
                    </div>
                    <div className="text-muted-foreground">
                      {att.kind}
                      {att.filename ? ` · ${att.filename}` : ""}
                      {att.url ? (
                        <>
                          {" · "}
                          <a className="text-primary underline" href={att.url} target="_blank" rel="noreferrer">
                            Download
                          </a>
                        </>
                      ) : null}
                    </div>
                    {att.notes ? <div className="text-xs">{att.notes}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === "clarifications" ? (
        <div className="grid gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Thread</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.clarifications || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No clarifications yet.</p>
              ) : (
                <ul className="grid gap-2 text-sm">
                  {data.clarifications.map((msg) => (
                    <li key={msg.id} className="rounded-lg border border-border px-3 py-2">
                      <div className="text-xs text-muted-foreground">
                        {msg.author_kind} · {msg.author_name} · {msg.vendor_name} · {String(msg.created_at || "")}
                      </div>
                      <div className="whitespace-pre-wrap">{msg.body}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          {canEnter ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Send clarification</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                <select
                  className="rounded-lg border border-input px-2.5 py-1.5 text-sm"
                  value={clarifyVendorRfqId}
                  onChange={(e) => setClarifyVendorRfqId(e.target.value)}
                >
                  <option value="">Select vendor RFQ…</option>
                  {quotes.map((q) => (
                    <option key={q.id} value={q.vendor_rfq_id || ""}>
                      {q.vendor_name}
                    </option>
                  ))}
                </select>
                <textarea
                  className="min-h-[80px] rounded-lg border border-input px-2.5 py-1.5 text-sm"
                  value={clarifyBody}
                  onChange={(e) => setClarifyBody(e.target.value)}
                  placeholder="Message to vendor (no customer PO details)"
                />
                <Button type="button" variant="outline" disabled={saving} onClick={sendClarification}>
                  Send
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      {tab === "pos" ? (
        <div className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer PO (internal)</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                <p className="text-xs text-muted-foreground">
                  Vendors never see this PO. It links the customer request to your internal requisition.
                </p>
                <input
                  className="rounded-lg border border-input px-2.5 py-1.5 text-sm"
                  placeholder="Customer PO number"
                  value={customerPo.order_number}
                  disabled={!canEnter}
                  onChange={(e) => setCustomerPo((p) => ({ ...p, order_number: e.target.value }))}
                />
                <input
                  type="date"
                  className="rounded-lg border border-input px-2.5 py-1.5 text-sm"
                  value={customerPo.order_date}
                  disabled={!canEnter}
                  onChange={(e) => setCustomerPo((p) => ({ ...p, order_date: e.target.value }))}
                />
                <input
                  className="rounded-lg border border-input px-2.5 py-1.5 text-sm"
                  placeholder="Total cents (optional)"
                  value={customerPo.total_cents}
                  disabled={!canEnter}
                  onChange={(e) => setCustomerPo((p) => ({ ...p, total_cents: e.target.value }))}
                />
                {canEnter ? (
                  <Button type="button" variant="outline" disabled={saving} onClick={linkCustomerPo}>
                    Link customer PO
                  </Button>
                ) : null}
                {data.customer_po ? (
                  <p className="text-sm">
                    Linked: <strong>{data.customer_po.order_number}</strong> ({data.customer_po.status})
                  </p>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sales invoice to customer</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                <p className="text-xs text-muted-foreground">
                  Edit sell prices (cents) before creating the invoice. Vendors never see this.
                </p>
                {(data.sales_invoices || []).length === 0 ? (
                  <>
                    {(data.proposed_invoice_lines || []).length > 0 ? (
                      <div className="grid gap-2">
                        {data.proposed_invoice_lines.map((row) => (
                          <div key={row.purchase_requisition_line_id} className="grid gap-1 rounded-md border border-border p-2 md:grid-cols-4">
                            <div className="md:col-span-2 text-sm">
                              <div className="font-medium">{row.description}</div>
                              <div className="text-xs text-muted-foreground">
                                Cost {money(row.cost_cents, currency)} · via {row.vendor_name}
                              </div>
                            </div>
                            <label className="grid gap-0.5 text-xs">
                              <span className="text-muted-foreground">Sell amount (cents)</span>
                              <input
                                className="rounded border border-input px-2 py-1 text-sm"
                                value={sellPrices[row.purchase_requisition_line_id] ?? ""}
                                disabled={!canEnter}
                                onChange={(e) =>
                                  setSellPrices((p) => ({
                                    ...p,
                                    [row.purchase_requisition_line_id]: e.target.value
                                  }))
                                }
                              />
                            </label>
                            <div className="text-sm self-end">
                              {money(Number(sellPrices[row.purchase_requisition_line_id] || 0), currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Award quotes first to propose invoice lines.</p>
                    )}
                    {canEnter ? (
                      <Button
                        type="button"
                        disabled={saving || !(data.proposed_invoice_lines || []).length}
                        onClick={createSalesInvoice}
                      >
                        Create sales invoice
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <ul className="grid gap-2 text-sm">
                    {data.sales_invoices.map((inv) => (
                      <li key={inv.id} className="rounded-lg border border-border px-3 py-2">
                        <div className="font-medium">{inv.invoice_number}</div>
                        <div className="text-muted-foreground">
                          {money(inv.total_cents, inv.currency)} · {inv.status} · {inv.customer_name}
                        </div>
                        <a className="text-primary underline" href={`/sales/invoices`}>
                          Open in Sales → Invoices
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vendor POs → receipt → vendor bills</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.vendor_pos || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No vendor POs yet. Award quotes, then issue POs.</p>
              ) : (
                <ul className="grid gap-3 text-sm">
                  {data.vendor_pos.map((po) => (
                    <li key={po.id} className="rounded-lg border border-border px-3 py-2">
                      <div className="font-medium">
                        {po.order_number} · {po.vendor_name}
                      </div>
                      <div className="text-muted-foreground">
                        {money(po.total_cents, po.currency)} · PO {po.status} · Delivery {po.delivery_status || "open"}
                        {po.bill_number ? ` · Bill ${po.bill_number}` : ""}
                      </div>

                      {(po.tax_invoices || []).length > 0 ? (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Vendor tax invoice(s):{" "}
                          {po.tax_invoices.map((ti) => (
                            <span key={ti.id} className="mr-2">
                              {ti.invoice_number}
                              {ti.url ? (
                                <>
                                  {" "}
                                  <a className="text-primary underline" href={ti.url} target="_blank" rel="noreferrer">
                                    file
                                  </a>
                                </>
                              ) : null}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {(po.lines || []).length > 0 ? (
                        <div className="mt-2 grid gap-1">
                          {po.lines.map((line) => (
                            <div key={line.id} className="grid items-center gap-2 rounded bg-muted/40 px-2 py-1 md:grid-cols-5">
                              <div className="md:col-span-2 text-xs">
                                {line.description}
                                <div className="text-muted-foreground">
                                  Ordered {line.quantity} {line.unit}
                                  {Number(line.quantity_variance) !== 0
                                    ? ` · variance ${line.quantity_variance}`
                                    : ""}
                                </div>
                              </div>
                              <label className="grid gap-0.5 text-xs">
                                <span className="text-muted-foreground">Received qty</span>
                                <input
                                  className="rounded border border-input px-2 py-1 text-sm"
                                  value={receiptDrafts[line.id] ?? String(line.quantity_received ?? "0")}
                                  disabled={!canEnter || Boolean(po.bill_id)}
                                  onChange={(e) =>
                                    setReceiptDrafts((d) => ({ ...d, [line.id]: e.target.value }))
                                  }
                                />
                              </label>
                              <div className="text-xs text-muted-foreground self-end">
                                Recv amt {money(line.received_amount_cents, po.currency)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {canEnter ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {!po.bill_id ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8"
                                disabled={saving}
                                onClick={() => markReceived(po.id)}
                              >
                                Save receipt
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8"
                                disabled={saving}
                                onClick={() => markReceived(po.id, { full: true })}
                              >
                                Receive all
                              </Button>
                              <Button type="button" className="h-8" disabled={saving} onClick={() => createVendorBill(po.id)}>
                                Create vendor bill
                              </Button>
                            </>
                          ) : (
                            <a className="text-primary underline" href={`/purchases/bills`}>
                              Open in Purchases → Bills
                            </a>
                          )}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  )
}
